import {ApiClient} from '@hconnect/apiclient'
import {type Attachment, type AttachmentFile} from '@hconnect/common/types'
import {useQuery} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect, useRef} from 'react'

export const AttachmentFilesQueryKey = 'attachmentFiles'

type Params = {
  attachments: Attachment[]
  /** Use preview URL if available (for images) */
  isPreview: boolean
}

const fetchAttachmentFile = async (
  attachment: Attachment,
  isPreview: boolean
): Promise<AttachmentFile> => {
  const {id, fileName, url, mediaType, previewUrl} = attachment
  const fileUrl = isPreview && previewUrl ? previewUrl : url
  const response = await ApiClient.axiosInstance.get<ArrayBuffer>(`/documents/${fileUrl}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': mediaType,
      Accept: mediaType
    }
  })
  const blobUrl = URL.createObjectURL(new Blob([response.data]))
  return {
    id,
    name: fileName,
    contentType: mediaType,
    preview: blobUrl,
    isImage: mediaType.includes('image')
  }
}

export const useAttachmentFilesQuery = ({attachments, isPreview}: Params) => {
  const trackedUrlsRef = useRef<string[]>([])

  const query = useQuery<AttachmentFile[], AxiosError>({
    queryKey: [AttachmentFilesQueryKey, attachments.map((a) => a.url), isPreview],
    queryFn: () =>
      Promise.all(attachments.map((attachment) => fetchAttachmentFile(attachment, isPreview))),
    enabled: attachments.length > 0
  })

  useEffect(() => {
    const newUrls = (query.data ?? []).map((f) => f.preview)
    trackedUrlsRef.current.forEach((u) => URL.revokeObjectURL(u))
    trackedUrlsRef.current = newUrls
  }, [query.data])

  useEffect(() => {
    return () => {
      trackedUrlsRef.current.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [])

  return query
}
